import { useQuery } from "@apollo/client";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Empty, Segmented } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import Breadcrumb from "../components/Breadcrumb";
import Card from "../components/Card";
import LeaderboardRow from "../components/leaderboard/LeaderboardRow";
import Podium from "../components/leaderboard/Podium";
import { LeaderboardSkeleton } from "../components/states";
import { GET_COMPANY_LEADERBOARD } from "../graphql/leaderboardQueries";

// Months are bucketed in UTC server-side, so the stepper works in UTC too —
// otherwise a local-timezone "current month" could point past the last bucket.
const currentMonthStr = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

const stepMonth = (monthStr, delta) => {
  const [y, m] = monthStr.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (monthStr) => {
  const [y, m] = monthStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("default", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const METRICS = [
  { value: "listings", label: "Listings" },
  { value: "matches", label: "Matches" },
  { value: "openHouses", label: "Open Houses" },
];

const Leaderboard = () => {
  const { companyId } = useParams();
  const [metric, setMetric] = useState("listings");
  const [month, setMonth] = useState(currentMonthStr());

  const { data, error, refetch } = useQuery(GET_COMPANY_LEADERBOARD, {
    variables: { companyId, metric, month },
    skip: !companyId,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const board = data?.getCompanyLeaderboard;
  const entries = board?.entries ?? null; // null = not resolved yet
  const firstLoad = entries === null && !error;

  const atCurrentMonth = month >= currentMonthStr();
  const atMinMonth = board?.minMonth != null && month <= board.minMonth;

  const renderBody = () => {
    if (firstLoad) return <LeaderboardSkeleton />;

    if (error && !entries?.length)
      return (
        <div className="flex flex-col items-center gap-3 py-12">
          <span className="text-secondary">Unable to load leaderboard</span>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      );

    if (!entries?.length)
      return (
        <>
          <Empty
            className="py-12"
            description={
              <div>
                <div className="text-black">No activity this month yet</div>
                <div className="text-secondary text-sm">
                  Listings, matches and open houses this month will rank agents here.
                </div>
              </div>
            }
          />
          {board?.currentUser && <LeaderboardRow entry={board.currentUser} pinned />}
        </>
      );

    return (
      <>
        <Podium top={entries.slice(0, 3)} />
        <div>
          {entries.slice(3).map((entry) => (
            <LeaderboardRow key={entry.userId} entry={entry} />
          ))}
        </div>
        {board?.currentUser && <LeaderboardRow entry={board.currentUser} pinned />}
      </>
    );
  };

  return (
    <div id="leaderboard">
      <Breadcrumb pageName="Leaderboard" />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            {/* Month stepper */}
            <div className="mb-4 flex items-center justify-center gap-6">
              <Button
                shape="circle"
                icon={<LeftOutlined />}
                disabled={atMinMonth}
                onClick={() => setMonth((mo) => stepMonth(mo, -1))}
              />
              <span className="text-base font-semibold text-black">
                {monthLabel(month)}
              </span>
              <Button
                shape="circle"
                icon={<RightOutlined />}
                disabled={atCurrentMonth}
                onClick={() => setMonth((mo) => stepMonth(mo, 1))}
              />
            </div>

            {/* Metric tabs */}
            <div className="mb-2 flex justify-center">
              <Segmented
                options={METRICS}
                value={metric}
                onChange={setMetric}
                size="large"
              />
            </div>
          </div>

          <div key={firstLoad ? "skeleton" : "data"} className="animate-riseIn">
            {renderBody()}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;
